<template>
  <div class="flex flex-col items-center mb-10">
    <button
      @click="openModal"
      :disabled="disabled"
      class="bg-green-500 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded"
    >
      <span v-if="disabled">{{ uiT('excel.loadingTemplate') }}</span>
      <span v-else>{{ uiT('excel.downloadTemplate', { categories: `${labelStaff}, ${labelAsistentes}, ${labelExpositor}` }) }}</span>
    </button>

    <div v-if="mostrarModal" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div class="bg-white p-6 rounded shadow-lg w-full max-w-md">
        <h3 class="text-lg font-bold mb-2">{{ uiT('excel.beforeDownload') }}</h3>
        <p class="mb-4">{{ uiT('excel.requiredFields') }}</p>

        <ul class="list-disc list-inside text-sm mb-4">
          <li v-for="c in camposRequeridos" :key="c.label">
            {{ c.nombre }}
          </li>
        </ul>

        <p class="text-sm text-gray-600 mb-4">
          {{ uiT('excel.templateHelp') }}
        </p>

        <div class="flex justify-end gap-4">
          <button
            @click="descargar"
            class="bg-green-600 hover:bg-green-800 text-white px-4 py-2 rounded"
          >
            {{ uiT('excel.downloadTemplateAction') }}
          </button>
          <button
            @click="mostrarModal = false"
            class="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
          >
            {{ uiT('common.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ExcelJS from "exceljs";
import { PAISES } from "@/utils/paises";
import { userManagementI18nMixin } from "@/views/usuarios/i18n/userManagementI18n.js";
import { getEventFieldLabel } from "@/views/usuarios/i18n/eventFieldLabels.js";

const humanize = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (m) => m.toUpperCase());

const sortConfiguredOrder = (list = []) => {
  return (list || [])
    .map((item, idx) => ({ item, idx }))
    .sort((a, b) => {
      const ao = Number(a.item?.orden);
      const bo = Number(b.item?.orden);
      const aHas = Number.isFinite(ao) && ao > 0;
      const bHas = Number.isFinite(bo) && bo > 0;

      if (aHas && bHas && ao !== bo) return ao - bo;
      if (aHas !== bHas) return aHas ? -1 : 1;
      return a.idx - b.idx;
    })
    .map(({ item }, idx) => ({ ...item, orden: idx + 1 }));
};

// Si un multicheck tiene demasiadas opciones, no conviene “explotarlo” en columnas
const MAX_MULTI_OPTION_COLS = 12;

export default {
  name: "DownloadButton",
  mixins: [userManagementI18nMixin],
  props: {
    camposObligatorios: { type: Array, required: true },
    labelStaff: String,
    labelAsistentes: String,
    labelExpositor: String,
    language: { type: String, default: "es" },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return { mostrarModal: false };
  },
  computed: {
    camposActivos() {
      return (this.camposObligatorios || [])
        .filter((c) => c && c.activo && c.label)
        .map((c) => { 
          const isPais = c.label === "pais";
          return {
          label: c.label,
          nombre: getEventFieldLabel(c, this.language) || humanize(c.label),
          requerido: !!c.requerido,
          mandatorio: !!c.mandatorio,
          custom: !!c.custom,
          tipo_ui: isPais ? "select" : (c.tipo_ui || "input"),
          opciones: isPais ? PAISES : (Array.isArray(c.opciones) ? c.opciones : []),
          orden: Number(c.orden) > 0 ? Number(c.orden) : null,
        }});
    },
    camposRequeridos() {
      return this.camposActivos.filter((c) => c.requerido || c.mandatorio);
    },
    camposOrdenados() {
      return sortConfiguredOrder(this.camposActivos.slice());
    },
  },
  methods: {
    openModal() {
      if (this.disabled) return;
      this.mostrarModal = true;
    },

    _noteList(opts = [], limit = 25) {
      const a = (opts || []).slice(0, limit);
      return (opts || []).length > limit ? `${a.join(", ")}…` : a.join(", ");
    },

    _pickOptionText(o) {
      if (o == null) return "";
      if (typeof o === "string" || typeof o === "number") return String(o).trim();
      if (typeof o === "object") return String(o.value ?? o.label ?? o.nombre ?? "").trim();
      return "";
    },
    _normOptions(opciones) {
      const arr = Array.isArray(opciones) ? opciones : [];
      return arr.map(this._pickOptionText).filter(Boolean);
    },
    _slugify(s) {
      return String(s || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 40);
    },
    _colLetter(n) {
      // 1 -> A, 2 -> B, ...
      let s = "";
      while (n > 0) {
        const m = (n - 1) % 26;
        s = String.fromCharCode(65 + m) + s;
        n = Math.floor((n - 1) / 26);
      }
      return s;
    },

    async descargar() {
      this.mostrarModal = false;

      const cols = this.camposOrdenados;

      const wb = new ExcelJS.Workbook();
      wb.creator = "Invitaciones Sponsors";
      wb.created = new Date();

      const templateSheetName = this.language === "en" ? "Template" : "Plantilla";
      const listSheetName = this.language === "en" ? "Lists" : "Listas";
      const ws = wb.addWorksheet(templateSheetName);

      const wsList = wb.addWorksheet(listSheetName);
      wsList.state = "hidden";
      const listSheetRef = `'${listSheetName}'`;

      // --- Listas base ---
      // tipos de documento en columna A
      wsList.getCell("A1").value = "tipo_de_documento";
      wsList.getCell("A2").value = "CC";
      wsList.getCell("A3").value = "CE";
      wsList.getCell("A4").value = this.language === "en" ? "Passport" : "Pasaporte";
      const tipoDocRange = `${listSheetRef}!$A$2:$A$4`;

      // lista SI/NO para multicheck (columna B por defecto, pero si se ocupa, la movemos)
      let listColCursor = 2; // B
      const siNoCol = listColCursor++;
      const siNoLetter = this._colLetter(siNoCol);
      wsList.getCell(`${siNoLetter}1`).value = "SI_NO";
      wsList.getCell(`${siNoLetter}2`).value = this.language === "en" ? "YES" : "SI";
      wsList.getCell(`${siNoLetter}3`).value = this.language === "en" ? "NO" : "NO";
      const siNoRange = `${listSheetRef}!$${siNoLetter}$2:$${siNoLetter}$3`;

      // --- Construcción de columnas del template ---
      // (aquí explotamos multicheck en columnas si tiene pocas opciones)
      const templateCols = [];
      const colMeta = []; // por índice (1-based) info para validaciones y notas

      for (const f of cols) {
        const ui = String(f.tipo_ui || "input").toLowerCase();
        const opts = this._normOptions(f.opciones);

        if (ui === "multicheck" && opts.length > 0 && opts.length <= MAX_MULTI_OPTION_COLS) {
          for (const opt of opts) {
            const key = `${f.label}__${this._slugify(opt)}`;
            const header = `${f.nombre} — ${opt}`;
            templateCols.push({
              header,
              key,
              width: Math.max(18, Math.min(52, header.length + 4)),
            });
            colMeta.push({ kind: "multicheck_option", baseLabel: f.label, option: opt });
          }
        } else {
          templateCols.push({
            header: f.nombre,
            key: f.label,
            width: Math.max(18, Math.min(42, (f.nombre || "").length + 6)),
          });

          colMeta.push({
            kind: ui === "select" ? "select" : ui === "multicheck" ? "multicheck_csv" : "input",
            label: f.label,
            nombre: f.nombre,
            opciones: opts,
          });
        }
      }

      ws.columns = templateCols;

      ws.views = [{ state: "frozen", ySplit: 1 }];
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).alignment = { vertical: "middle", horizontal: "center", wrapText: true };

      // 100 filas vacías (con keys del template)
      const emptyObj = {};
      templateCols.forEach((c) => (emptyObj[c.key] = ""));
      for (let i = 0; i < 100; i++) ws.addRow({ ...emptyObj });

      // --- Crear listas dinámicas para SELECT ---
      // Para cada select, escribimos una columna en Listas y aplicamos DV a la columna correspondiente
      for (let cIdx = 1; cIdx <= templateCols.length; cIdx++) {
        const meta = colMeta[cIdx - 1];
        const colDef = templateCols[cIdx - 1];

        // tipo_de_documento: usa lista base
        if (colDef.key === "tipo_de_documento") {
          ws.getCell(1, cIdx).note = this.uiT("excel.documentTypeNote");

          for (let r = 2; r <= 101; r++) {
            ws.getCell(r, cIdx).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [tipoDocRange],
              showErrorMessage: true,
              errorTitle: this.uiT("excel.invalidValueTitle"),
              error: this.uiT("excel.documentTypeError"),
            };
          }
          continue;
        }

        // select dinámico
        if (meta.kind === "select" && Array.isArray(meta.opciones) && meta.opciones.length) {
          const colNum = listColCursor++;
          const letter = this._colLetter(colNum);

          wsList.getCell(`${letter}1`).value = meta.label;
          meta.opciones.forEach((opt, i) => {
            wsList.getCell(`${letter}${2 + i}`).value = opt;
          });

          const range = `${listSheetRef}!$${letter}$2:$${letter}$${1 + meta.opciones.length}`;

          ws.getCell(1, cIdx).note = this.uiT("excel.allowedValuesNote", { values: this._noteList(meta.opciones, 25) });


          for (let r = 2; r <= 101; r++) {
            ws.getCell(r, cIdx).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [range],
              showErrorMessage: true,
              errorTitle: this.uiT("excel.invalidValueTitle"),
              error: this.uiT("excel.selectAllowedValue"),
            };
          }
        }

        // multicheck explotado en columnas: SI/NO
        if (meta.kind === "multicheck_option") {
          ws.getCell(1, cIdx).note = this.uiT("excel.multicheckOptionNote");

          for (let r = 2; r <= 101; r++) {
            ws.getCell(r, cIdx).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [siNoRange],
              showErrorMessage: true,
              errorTitle: this.uiT("excel.invalidValueTitle"),
              error: this.uiT("excel.yesNoError"),
            };
          }
        }

        // multicheck CSV (demasiadas opciones): una sola columna con instrucción
        if (meta.kind === "multicheck_csv") {
          if (meta.opciones && meta.opciones.length) {
            ws.getCell(1, cIdx).note =
              this.uiT("excel.multicheckAllowedNote", { values: meta.opciones.join(", ") });
          } else {
            ws.getCell(1, cIdx).note = this.uiT("excel.multicheckNote");
          }
        }
      }

      // Descargar
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = this.uiT("excel.templateFilename");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
ul { list-style: disc; padding-left: 18px; }
</style>
